import { useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  useListInbox,
  useGetInbox,
  useArchiveInbox,
  useAnalyzeInbox,
  useAcceptSuggestion,
  useRejectSuggestion,
  useUpdateSuggestion,
  useCreateInbox,
  useTranscribeAudio,
  useListProjects,
  getListInboxQueryKey,
  getGetInboxQueryKey,
  getGetTodayQueryKey,
  getListProjectsQueryKey,
  type InboxItem,
  type InboxItemWithSuggestions,
  type SuggestedAction,
  type SuggestedActionPayload,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Mic,
  Square,
  FileText,
  Mail,
  Check,
  X,
  Sparkles,
  Archive,
  Inbox as InboxIcon,
  Pencil,
  CheckCheck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type SourceType = "text" | "voice" | "email";

function parseEmailRawText(rawText: string | null | undefined): {
  fromName: string;
  subject: string;
} | null {
  if (!rawText) return null;
  const lines = rawText.split("\n");
  const senderLine = lines.find((l) => l.startsWith("Email reçu de : "));
  const subjectLine = lines.find((l) => l.startsWith("Objet : "));
  const fromName = senderLine
    ? senderLine.replace("Email reçu de : ", "").replace(/<[^>]+>/, "").trim()
    : "";
  const subject = subjectLine ? subjectLine.replace("Objet : ", "").trim() : "";
  if (!fromName && !subject) return null;
  return { fromName, subject };
}

function suggestionTitle(payload: SuggestedActionPayload, fallback: string): string {
  const t = (payload.title as string | undefined) ?? (payload.name as string | undefined);
  return t && t.trim() ? t : fallback;
}

function suggestionDescription(payload: SuggestedActionPayload): string {
  const d = (payload.description as string | undefined) ?? (payload.content as string | undefined);
  return d ?? "";
}

function payloadPriority(payload: SuggestedActionPayload): "low" | "medium" | "high" {
  const p = payload.priority as string | undefined;
  if (p === "low" || p === "medium" || p === "high") return p;
  return "medium";
}

function payloadDueDate(payload: SuggestedActionPayload): string {
  const d = payload.due_date as string | undefined;
  if (!d) return "";
  // Accepts ISO date or full ISO string; normalize to YYYY-MM-DD for <input type="date">
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(d);
  return m ? m[1] : "";
}

function payloadProjectId(payload: SuggestedActionPayload): string {
  const p = payload.projectId as string | undefined;
  return p ?? "";
}

function SuggestionRow({
  suggestion,
  inboxItemId,
}: {
  suggestion: SuggestedAction;
  inboxItemId: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(suggestionTitle(suggestion.payload, t("inbox_page.no_title")));
  const [description, setDescription] = useState(suggestionDescription(suggestion.payload));
  const [priority, setPriority] = useState<"low" | "medium" | "high">(
    payloadPriority(suggestion.payload),
  );
  const [dueDate, setDueDate] = useState<string>(payloadDueDate(suggestion.payload));
  const [projectId, setProjectId] = useState<string>(payloadProjectId(suggestion.payload));

  const projectsQuery = useListProjects(undefined, {
    query: { queryKey: getListProjectsQueryKey(), enabled: editing, staleTime: 60_000 },
  });

  const update = useUpdateSuggestion();
  const accept = useAcceptSuggestion();
  const reject = useRejectSuggestion();

  const isAccepted = suggestion.status === "accepted";
  const isRejected = suggestion.status === "rejected";
  const isFinal = isAccepted || isRejected;
  const supportsPriority =
    suggestion.type === "task" ||
    suggestion.type === "project" ||
    suggestion.type === "reminder";
  const supportsDueDate =
    suggestion.type === "task" ||
    suggestion.type === "reminder" ||
    suggestion.type === "event";
  const supportsProject = suggestion.type === "task";

  const resetEdits = () => {
    setTitle(suggestionTitle(suggestion.payload, t("inbox_page.no_title")));
    setDescription(suggestionDescription(suggestion.payload));
    setPriority(payloadPriority(suggestion.payload));
    setDueDate(payloadDueDate(suggestion.payload));
    setProjectId(payloadProjectId(suggestion.payload));
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: getGetInboxQueryKey(inboxItemId) });
    queryClient.invalidateQueries({ queryKey: getListInboxQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() });
  };

  const handleSaveEdit = async () => {
    try {
      const nextPayload: SuggestedActionPayload = {
        ...suggestion.payload,
        title,
        description,
      };
      if (supportsPriority) nextPayload.priority = priority;
      if (supportsDueDate) {
        if (dueDate) nextPayload.due_date = dueDate;
        else delete nextPayload.due_date;
      }
      if (supportsProject) {
        if (projectId) nextPayload.projectId = projectId;
        else delete nextPayload.projectId;
      }
      await update.mutateAsync({
        id: suggestion.id,
        data: { payload: nextPayload },
      });
      setEditing(false);
      invalidateAll();
      toast({ title: t("inbox_page.suggestion_updated_toast") });
    } catch {
      toast({ title: t("common.update_error"), variant: "destructive" });
    }
  };

  const handleAccept = async () => {
    try {
      await accept.mutateAsync({ id: suggestion.id });
      invalidateAll();
      toast({ title: t("inbox_page.suggestion_accepted_toast") });
    } catch {
      toast({ title: t("common.accept_error"), variant: "destructive" });
    }
  };

  const handleReject = async () => {
    try {
      await reject.mutateAsync({ id: suggestion.id });
      invalidateAll();
      toast({ title: t("inbox_page.suggestion_rejected_toast") });
    } catch {
      toast({ title: t("common.reject_error"), variant: "destructive" });
    }
  };

  return (
    <div
      className={`p-3 border rounded-xl bg-card flex flex-col gap-2 ${
        isAccepted ? "border-blue-500/40 bg-blue-500/5" : ""
      } ${isRejected ? "opacity-50 line-through" : ""}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge className="px-1.5 py-0 h-5 text-[10px] font-bold uppercase">
              {t(`item_type.${suggestion.type}`, suggestion.type)}
            </Badge>
            {isAccepted && (
              <Badge variant="outline" className="text-[10px] text-blue-600 border-blue-500/40">
                {t("common.validated_badge")}
              </Badge>
            )}
            {isRejected && (
              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                {t("common.rejected_badge")}
              </Badge>
            )}
          </div>
          {editing ? (
            <div className="space-y-2 mt-1">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("common.no_title")}
                className="h-8 text-sm"
                aria-label={t("inbox_page.title_aria")}
              />
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("common.no_title")}
                className="text-sm resize-none min-h-[60px]"
                aria-label={t("inbox_page.desc_aria")}
              />
              {(supportsPriority || supportsDueDate) && (
                <div className="grid grid-cols-2 gap-2">
                  {supportsPriority && (
                    <div className="space-y-1">
                      <label
                        htmlFor={`priority-${suggestion.id}`}
                        className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                      >
                        {t("inbox_page.priority_label")}
                      </label>
                      <Select
                        value={priority}
                        onValueChange={(v) => setPriority(v as "low" | "medium" | "high")}
                      >
                        <SelectTrigger
                          id={`priority-${suggestion.id}`}
                          className="h-8 text-xs"
                          aria-label={t("inbox_page.priority_label")}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">{t("inbox_page.priority_low")}</SelectItem>
                          <SelectItem value="medium">{t("inbox_page.priority_medium")}</SelectItem>
                          <SelectItem value="high">{t("inbox_page.priority_high")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {supportsDueDate && (
                    <div className="space-y-1">
                      <label
                        htmlFor={`due-${suggestion.id}`}
                        className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                      >
                        {t("inbox_page.deadline_label")}
                      </label>
                      <Input
                        id={`due-${suggestion.id}`}
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        className="h-8 text-xs"
                        aria-label={t("inbox_page.deadline_label")}
                      />
                    </div>
                  )}
                </div>
              )}
              {supportsProject && (
                <div className="space-y-1">
                  <label
                    htmlFor={`project-${suggestion.id}`}
                    className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                  >
                    {t("inbox_page.project_label")}
                  </label>
                  <Select
                    value={projectId || "__none__"}
                    onValueChange={(v) => setProjectId(v === "__none__" ? "" : v)}
                  >
                    <SelectTrigger
                      id={`project-${suggestion.id}`}
                      className="h-8 text-xs"
                      aria-label={t("inbox_page.project_label")}
                    >
                      <SelectValue placeholder={t("inbox_page.no_project")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t("inbox_page.no_project")}</SelectItem>
                      {(projectsQuery.data ?? []).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="text-sm font-medium text-foreground">{title}</div>
              {description && (
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                  {description}
                </p>
              )}
            </>
          )}
        </div>

        {!isFinal && (
          <div className="flex gap-1 shrink-0">
            {editing ? (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={handleSaveEdit}
                  disabled={update.isPending}
                  aria-label={t("inbox_page.save_aria")}
                  title={t("inbox_page.save_title")}
                >
                  {update.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => {
                    setEditing(false);
                    resetEdits();
                  }}
                  aria-label={t("inbox_page.cancel_aria")}
                  title={t("inbox_page.cancel_title")}
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => setEditing(true)}
                  aria-label="Modifier la suggestion"
                  title="Modifier"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={handleAccept}
                  disabled={accept.isPending}
                  aria-label="Accepter la suggestion"
                  title="Accepter"
                >
                  {accept.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={handleReject}
                  disabled={reject.isPending}
                  aria-label="Rejeter la suggestion"
                  title="Rejeter"
                >
                  {reject.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InboxItemCard({ item }: { item: InboxItem }) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const detailQuery = useGetInbox(item.id, {
    query: {
      queryKey: getGetInboxQueryKey(item.id),
      enabled: item.status === "analyzed",
    },
  });
  const detail: InboxItemWithSuggestions | undefined = detailQuery.data;
  const pendingSuggestions = (detail?.suggestions ?? []).filter(
    (s) => s.status === "pending",
  );

  const analyzeInbox = useAnalyzeInbox();
  const archiveInbox = useArchiveInbox();
  const acceptSuggestion = useAcceptSuggestion();
  const rejectSuggestion = useRejectSuggestion();
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);
  const [isRejectingAll, setIsRejectingAll] = useState(false);
  const processedSuggestionIds = useRef<Set<string>>(new Set());
  const processedRejectedIds = useRef<Set<string>>(new Set());

  const invalidateAll = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListInboxQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetInboxQueryKey(item.id) }),
      queryClient.invalidateQueries({ queryKey: getGetTodayQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() }),
    ]);
  }, [queryClient, item.id]);

  const handleAnalyze = () => {
    analyzeInbox.mutate(
      { id: item.id },
      {
        onSuccess: () => {
          invalidateAll();
          toast({ title: t("inbox_page.analyzed_toast") });
        },
        onError: () => toast({ title: t("inbox_page.analyze_error"), variant: "destructive" }),
      },
    );
  };

  const handleArchive = () => {
    archiveInbox.mutate(
      { id: item.id },
      {
        onSuccess: () => {
          invalidateAll();
          toast({ title: t("inbox_page.archived_toast") });
        },
        onError: () => toast({ title: t("inbox_page.archive_error"), variant: "destructive" }),
      },
    );
  };

  const handleAcceptAll = async () => {
    if (isAcceptingAll) return;
    const toAccept = pendingSuggestions.filter(
      (s) => s.status === "pending" && !processedSuggestionIds.current.has(s.id),
    );
    if (toAccept.length === 0) return;
    // Optimistically mark IDs before dispatching — prevents re-submission
    // in the stale-data window between allSettled and invalidation settling.
    toAccept.forEach((s) => processedSuggestionIds.current.add(s.id));
    setIsAcceptingAll(true);
    try {
      const results = await Promise.allSettled(
        toAccept.map((s) => acceptSuggestion.mutateAsync({ id: s.id })),
      );
      // Await invalidation so fresh data is loaded before button unlocks.
      await invalidateAll();
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        toast({ title: t("inbox_page.accept_all_toast", { count: succeeded }) });
      } else if (succeeded === 0) {
        // Remove failed IDs from the set so user can retry them.
        results.forEach((r, i) => {
          if (r.status === "rejected") processedSuggestionIds.current.delete(toAccept[i].id);
        });
        toast({ title: t("inbox_page.accept_all_error"), variant: "destructive" });
      } else {
        const failedTitles = results
          .map((r, i) => {
            if (r.status === "rejected") {
              processedSuggestionIds.current.delete(toAccept[i].id);
              return toAccept[i];
            }
            return null;
          })
          .filter((s): s is NonNullable<typeof s> => s !== null)
          .map((s) => suggestionTitle(s.payload, "?"))
          .join(", ");
        toast({
          title: t("inbox_page.accept_all_partial_toast", { count: succeeded, failed }),
          description: failedTitles || undefined,
          variant: "destructive",
        });
      }
    } finally {
      setIsAcceptingAll(false);
    }
  };

  const handleRejectAll = async () => {
    if (isRejectingAll) return;
    const toReject = pendingSuggestions.filter(
      (s) => s.status === "pending" && !processedRejectedIds.current.has(s.id),
    );
    if (toReject.length === 0) return;
    toReject.forEach((s) => processedRejectedIds.current.add(s.id));
    setIsRejectingAll(true);
    try {
      const results = await Promise.allSettled(
        toReject.map((s) => rejectSuggestion.mutateAsync({ id: s.id })),
      );
      await invalidateAll();
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        toast({ title: t("inbox_page.reject_all_toast", { count: succeeded }) });
      } else if (succeeded === 0) {
        results.forEach((r, i) => {
          if (r.status === "rejected") processedRejectedIds.current.delete(toReject[i].id);
        });
        toast({ title: t("inbox_page.reject_all_error"), variant: "destructive" });
      } else {
        const failedTitles = results
          .map((r, i) => {
            if (r.status === "rejected") {
              processedRejectedIds.current.delete(toReject[i].id);
              return toReject[i];
            }
            return null;
          })
          .filter((s): s is NonNullable<typeof s> => s !== null)
          .map((s) => suggestionTitle(s.payload, "?"))
          .join(", ");
        toast({
          title: t("inbox_page.reject_all_partial_toast", { count: succeeded, failed }),
          description: failedTitles || undefined,
          variant: "destructive",
        });
      }
    } finally {
      setIsRejectingAll(false);
    }
  };

  const sourceIcon =
    item.sourceType === "voice" ? (
      <Mic className="w-4 h-4" />
    ) : item.sourceType === "email" ? (
      <Mail className="w-4 h-4" />
    ) : (
      <FileText className="w-4 h-4" />
    );

  const isAnalyzing = analyzeInbox.isPending;
  const isArchiving = archiveInbox.isPending;
  const isLoadingDetail = item.status === "analyzed" && detailQuery.isLoading;

  // Expand/collapse for long non-email bodies.
  const displayedText = item.rawText || item.transcriptText || "";
  const lineCount = displayedText ? displayedText.split("\n").length : 0;
  const isLong = item.sourceType !== "email" && (displayedText.length > 600 || lineCount > 12);
  const [expanded, setExpanded] = useState(false);
  const emailParsed = item.sourceType === "email" ? parseEmailRawText(item.rawText) : null;

  return (
    <Card className="rounded-2xl overflow-hidden transition-all border-border shadow-sm mb-4">
      <CardContent className="p-0">
        <div className="p-5">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
              {sourceIcon}
              <span>
                {formatDistanceToNow(new Date(item.createdAt), {
                  addSuffix: true,
                  locale: dateLocale,
                })}
              </span>
              {item.aiDetectedType && (
                <Badge variant="outline" className="ml-1 font-normal text-xs py-0 h-5">
                  {item.aiDetectedType}
                </Badge>
              )}
              {item.status === "new" && (
                <Badge variant="outline" className="font-normal text-xs py-0 h-5">
                  {t("inbox_page.status_new")}
                </Badge>
              )}
              {item.status === "analyzed" && (
                <Badge
                  variant="outline"
                  className="font-normal text-xs py-0 h-5 border-primary/40 text-primary"
                >
                  {t("inbox_page.status_analyzed")}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={handleArchive}
                disabled={isArchiving}
                title={t("inbox_page.archive_title")}
                aria-label={t("inbox_page.archive_aria")}
              >
                {isArchiving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Archive className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          {emailParsed ? (
            <div className="mt-1" data-testid={`inbox-item-body-${item.id}`}>
              {emailParsed.fromName && (
                <div className="text-sm font-semibold text-foreground">{emailParsed.fromName}</div>
              )}
              {emailParsed.subject && (
                <div className="text-sm text-muted-foreground mt-0.5">{emailParsed.subject}</div>
              )}
            </div>
          ) : (
            <>
              <div
                className={`relative text-foreground text-sm leading-relaxed whitespace-pre-wrap font-medium ${
                  isLong && !expanded ? "max-h-48 overflow-hidden" : ""
                }`}
                data-testid={`inbox-item-body-${item.id}`}
              >
                {displayedText || t("inbox_page.empty_content")}
                {isLong && !expanded && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent"
                  />
                )}
              </div>
              {isLong && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setExpanded((v) => !v)}
                  data-testid={`button-toggle-expand-${item.id}`}
                >
                  {expanded ? (
                    <>
                      <ChevronUp className="w-3.5 h-3.5 mr-1" />
                      {t("inbox_page.see_less")}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3.5 h-3.5 mr-1" />
                      {t("inbox_page.see_more")}
                    </>
                  )}
                </Button>
              )}
            </>
          )}

          {item.aiSummary && (
            <div className="mt-4 p-3 bg-secondary/30 rounded-xl text-sm text-secondary-foreground border border-secondary">
              <div className="flex items-center gap-2 mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Sparkles className="w-3 h-3" /> {t("inbox_page.ai_summary")}
              </div>
              {item.aiSummary}
            </div>
          )}

          {item.status === "new" && (
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                className="w-full sm:w-auto"
              >
                {isAnalyzing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2 text-accent" />
                )}
                {isAnalyzing ? t("inbox_page.analyzing") : t("inbox_page.extract_actions_btn")}
              </Button>
            </div>
          )}

          {isLoadingDetail && (
            <div className="mt-5 space-y-2">
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-12 w-full rounded-xl" />
            </div>
          )}

          {detail && detail.suggestions.length > 0 && (
            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("inbox_page.suggestions_header", { count: detail.suggestions.length })}
                </h4>
                {pendingSuggestions.length > 1 && (
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs h-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={handleRejectAll}
                      disabled={isRejectingAll || isAcceptingAll}
                      data-testid="btn-reject-all"
                    >
                      {isRejectingAll ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <X className="w-3 h-3 mr-1" />
                      )}
                      {t("inbox_page.reject_all_btn")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs h-7"
                      onClick={handleAcceptAll}
                      disabled={isAcceptingAll || isRejectingAll}
                      data-testid="btn-accept-all"
                    >
                      {isAcceptingAll ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <CheckCheck className="w-3 h-3 mr-1" />
                      )}
                      {t("inbox_page.accept_all_btn")}
                    </Button>
                  </div>
                )}
              </div>
              {detail.suggestions.map((s) => (
                <SuggestionRow key={s.id} suggestion={s} inboxItemId={item.id} />
              ))}
            </div>
          )}

          {detail && detail.suggestions.length === 0 && item.status === "analyzed" && (
            <p className="mt-4 text-xs text-muted-foreground italic">
              {t("inbox_page.no_actions_detected")}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


export default function InboxPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<string>("open");

  const { data: allItems, isLoading, error } = useListInbox(
    statusFilter === "open" || statusFilter === "all" ? {} : { status: statusFilter },
  );
  const items: InboxItem[] = (() => {
    const list = allItems ?? [];
    if (statusFilter === "open") return list.filter((it) => it.status !== "archived");
    return list;
  })();

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto pb-24 md:pb-8">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground mb-1">{t("inbox_page.title")}</h1>
          <p className="text-muted-foreground">{t("inbox_page.subtitle")}</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] rounded-full" aria-label={t("inbox_page.filter_aria")}>
            <SelectValue placeholder={t("inbox_page.filter_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">{t("inbox_page.filter_open")}</SelectItem>
            <SelectItem value="new">{t("inbox_page.filter_new")}</SelectItem>
            <SelectItem value="analyzed">{t("inbox_page.filter_analyzed")}</SelectItem>
            <SelectItem value="archived">{t("inbox_page.filter_archived")}</SelectItem>
            <SelectItem value="all">{t("inbox_page.filter_all")}</SelectItem>
          </SelectContent>
        </Select>
      </header>

      <div className="space-y-4">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="rounded-2xl">
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-8 w-24" />
              </CardContent>
            </Card>
          ))
        ) : error ? (
          <div className="text-center p-8 text-destructive border border-destructive/20 rounded-2xl bg-destructive/5">
            {t("inbox_page.load_error")}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center p-12 border border-border/50 border-dashed rounded-3xl bg-secondary/20">
            <InboxIcon className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-1">{t("inbox_page.empty_title")}</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              {t("inbox_page.empty_desc")}
            </p>
          </div>
        ) : (
          items.map((item) => <InboxItemCard key={item.id} item={item} />)
        )}
      </div>
    </div>
  );
}
